import type { PaymentAttempt } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { ATTEMPT_STATUS_CLASS, ATTEMPT_STATUS_LABELS, formatCardRef } from '../../lib/payments';

interface AttemptHistoryProps {
  attempts: PaymentAttempt[];
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The audit history (CHO-754): every payment attempt, readable without a
 * database client — this is the record that answers "did we already pay
 * that?". The service does not join invoice property/biller onto attempts,
 * so rows are keyed by invoice id. Card references render as last four
 * digits only.
 */
export function AttemptHistory({ attempts }: AttemptHistoryProps) {
  if (attempts.length === 0) {
    return (
      <p className="text-gray-500 bg-white border border-gray-200 rounded-lg px-4 py-8 text-center">
        No payment attempts yet.
      </p>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th className="px-4 py-3 font-medium">Time</th>
            <th className="px-4 py-3 font-medium">Invoice</th>
            <th className="px-4 py-3 font-medium text-right">Amount</th>
            <th className="px-4 py-3 font-medium">Card</th>
            <th className="px-4 py-3 font-medium">Outcome</th>
            <th className="px-4 py-3 font-medium">Resolved by</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map((attempt) => (
            <tr key={attempt.id} className="border-b border-gray-50 last:border-0">
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatTimestamp(attempt.createdAt)}</td>
              <td className="px-4 py-3">
                <span className="font-medium text-gray-900">Invoice #{attempt.invoiceId}</span>
              </td>
              <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                {formatCurrency(attempt.amount, attempt.currency)}
              </td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatCardRef(attempt.cardLast4)}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${ATTEMPT_STATUS_CLASS[attempt.status] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}
                >
                  {ATTEMPT_STATUS_LABELS[attempt.status] ?? attempt.status}
                </span>
                {attempt.status === 'failed' && attempt.failureReason && (
                  <span className="block text-xs text-red-600 mt-1">{attempt.failureReason}</span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {attempt.resolvedBy ? (
                  <span>
                    {attempt.resolvedBy}
                    {attempt.resolvedAt && (
                      <span className="block text-xs text-gray-400">{formatTimestamp(attempt.resolvedAt)}</span>
                    )}
                    {attempt.resolvedNote && (
                      <span className="block text-xs text-gray-400 italic">{attempt.resolvedNote}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
